import React, { useState, useEffect } from "react";
import { HeaderComponent, TableComponent, BodyComponent, ActionComponent, trans, Switch, ButtonComponent } from '@comps/components';
import { makeRequest, notify, NOTIFICATION_TYPE } from '@utils/helpers';
import { getClinicsReq, changeStatusReq, deleteClinicReq } from './requests';
import { updateById, deleteById } from '@comps/commonFunctions';
import { Icon } from "@iconify/react";

import CreateClinic from "./components/CreateClinic";
import UpdateClinic from "./components/UpdateClinic";

const langKey = 'clinics';

const pageConfig = {
  headers: {
    title: "Clinics",
    breadcrumb: [
      {
        name: "Clinics",
        path: "/clinics"
      }
    ]
  }
}

const module = 'clinics';

const IndexClinics = () => {
  const [dataSource, setDataSource] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });

  useEffect(() => {
    getClinicsData();
  }, [pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  const getClinicsData = () => {
    makeRequest(setLoader, getClinicsReq, null, onSuccess, onError);
  }

  const onError = (error) => {
    console.log(error);
  }

  const onSuccess = (data) => {
    setDataSource(data.data);
    setTotalRecords(data.totalRecords);
  }

  const handleTableChange = (page, fil, sorter) => {
    let payload = {
      ...pagination,
      current: page.current,
      pageSize: page.pageSize,
      sortName: sorter.field || 'id',
      sortType: sorter.order === 'ascend' ? 'asc' : 'desc',
    };
    setPagination(payload);
  }

  const columns = [
    {
      title: trans(`common.name`),
      key: "identity",
      render: (record) => (
        <div className="identity-cell">
          <span className="identity-avatar">
            { (record.name || "?").charAt(0).toUpperCase() }
          </span>
          <span className="identity-meta">
            <span className="identity-name">{ record.name }</span>
            <span className="identity-sub">{ record.phone || "No phone on file" }</span>
          </span>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "—",
    },
    {
      title: trans(`common.actions`),
      key: "actions",
      align: "right",
      width: 110,
      render: (record) => record.id !== 0 && (
        <ActionComponent module={ module } each={ record } onEdit={ onEdit } onDelete={ onDelete } />
      ),
    },
  ];

  const onChangeStatus = (data, action) => {
    let payload = {
      id: data.id,
      status: action
    };
    makeRequest(setLoader, changeStatusReq, payload, onChangeStatusSuccess, onChangeStatusError);
  }

  const onChangeStatusSuccess = (data) => {
    let index = dataSource.findIndex(each => each.id === data.id);
    dataSource[index].is_active = data.is_active;
    setDataSource([...dataSource]);
  }

  const onChangeStatusError = (error) => {
    console.log(error);
  }

  const onCreate = () => {
    setChildComponent(<CreateClinic onCreated={ onCreated } onClosed={ onClosed } />);
  }

  const onCreated = (data) => {
    setDataSource([data, ...dataSource]);
    setChildComponent(null);
  }

  const onEdit = (record) => {
    setChildComponent(<UpdateClinic data={ record } onUpdated={ onUpdated } onClosed={ onClosed } />);
  }

  const onUpdated = (data) => {
    setDataSource([...updateById(dataSource, data)]);
    setChildComponent(null);
  }

  const onDelete = (record) => {
    makeRequest(setLoader, deleteClinicReq, record.id, onDeleteSuccess, onDeleteError);
  }

  // This used to be a no-op, so a clinic the server refused to delete —
  // one still holding users or cases — just stayed in the list with no
  // word as to why. The server's message names what is holding it.
  const onDeleteError = (data, res) => {
    notify("Clinic not deleted", res?.msg || "Something went wrong. Please try again.", NOTIFICATION_TYPE.ERROR);
  }

  const onDeleteSuccess = (data) => {
    setDataSource([...deleteById(dataSource, data)]);
  }

  const onClosed = () => {
    setChildComponent(null);
  }

  return (
    <>
      { childComponent }
      <HeaderComponent
        headers={ pageConfig.headers }
        description="The clinics that submit cases, and how to reach them."
      />
      <BodyComponent flush module={ module } onCreate={ onCreate }>
        <TableComponent
          loader={ loader }
          columns={ columns }
          dataSource={ dataSource }
          emptyState={ {
            icon: "tabler:building-hospital",
            title: "No clinics yet",
            description: "Add a clinic so its cases can be tracked here.",
          } }
          pagination={ { ...pagination, total: totalRecords } }
          onChange={ handleTableChange }
        />
      </BodyComponent>
    </>
  )

}

export default IndexClinics